import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Send } from "lucide-react";
import { api, errMsg, waLink } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const EMPTY = { name: "", phone: "", email: "", location: "", message: "" };
const FIELDS = [
  ["name", "Nombre y apellido", "text", true],
  ["phone", "Teléfono / WhatsApp", "tel", false],
  ["email", "Email", "email", true],
  ["location", "Localidad / País", "text", false],
];

function InfoRow({ icon: Icon, label, value, href, testid }) {
  return (
    <a href={href} target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={testid} className="flex gap-4 py-5 border-b border-white/10 group">
      <Icon className="text-[#C5A059] mt-1 shrink-0" size={20} />
      <span>
        <span className="block text-[10px] uppercase tracking-[0.25em] text-stone-400">{label}</span>
        <span className="block mt-1 text-stone-100 group-hover:text-[#C5A059] transition-colors">{value}</span>
      </span>
    </a>
  );
}

export default function Contacto() {
  const { settings } = useApp();
  const [f, setF] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", f);
      setF(EMPTY);
      setSent(true);
      toast.success("¡Gracias! Recibimos su consulta y le responderemos a la brevedad.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contacto-page" className="pt-28 sm:pt-32 pb-24">
      <section className="container-fv">
        <p className="eyebrow rise">Contacto</p>
        <h1 className="f-display mt-4 text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-[1.05] rise d1">Hablemos de su próximo programa genético</h1>
        <div className="mt-14 grid gap-8 lg:grid-cols-12">
          <form onSubmit={submit} data-testid="contacto-form" className="card-fv lg:col-span-7 p-6 sm:p-10 grid gap-5 sm:grid-cols-2">
            {FIELDS.map(([k, label, type, req]) => (
              <div key={k} className="space-y-2">
                <Label htmlFor={k}>{label}{req && " *"}</Label>
                <Input id={k} type={type} required={req} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
                  data-testid={`contacto-input-${k}`} className="h-12 bg-[#F8F5EE]/60" />
              </div>
            ))}
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="message">Mensaje *</Label>
              <Textarea id="message" required rows={5} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })}
                placeholder="Cuéntenos qué está buscando: embriones, semen, convenio de embriones, animales en pie…" data-testid="contacto-input-message" className="bg-[#F8F5EE]/60" />
            </div>
            <div className="sm:col-span-2 flex flex-wrap items-center gap-4">
              <button type="submit" disabled={sending} data-testid="contacto-form-submit-button" className="btn-fv btn-red disabled:opacity-60">
                <Send size={17} />{sending ? "Enviando…" : "Enviar consulta"}
              </button>
              {sent && <p className="text-sm text-[#1D3324]" data-testid="contacto-success-message">Consulta enviada correctamente.</p>}
            </div>
          </form>
          <aside className="lg:col-span-5 bg-[#1D3324] p-6 sm:p-10 relative grain">
            <div className="relative">
              <p className="f-display text-2xl text-stone-100">Datos de contacto</p>
              <div className="mt-4">
                <InfoRow icon={MessageCircle} label="WhatsApp" value="Escríbanos ahora" href={waLink(settings.whatsapp, "Hola, quisiera hacer una consulta.")} testid="contacto-whatsapp-link" />
                <InfoRow icon={Phone} label="Teléfono" value={settings.phone} href={`tel:${settings.phone.replace(/\s/g, "")}`} testid="contacto-phone" />
                <InfoRow icon={Mail} label="Email" value={settings.email} href={`mailto:${settings.email}`} testid="contacto-email" />
                <InfoRow icon={MapPin} label="Ubicación" value={settings.address} href={`https://www.google.com/maps?q=${encodeURIComponent(settings.address)}`} testid="contacto-address" />
              </div>
              <a href={waLink(settings.whatsapp, "Hola, quisiera hacer una consulta.")} target="_blank" rel="noreferrer" data-testid="contacto-whatsapp-button" className="btn-fv btn-wa mt-8 w-full justify-center">
                <MessageCircle size={18} />Consultar por WhatsApp
              </a>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
